// Imperative OpenLayers wrapper for the line map.
//
// The legacy `line_map.js` kept the map, layers and in-flight requests in
// closure variables and referenced `ol.*` from the vendored Sprockets global.
// The Vue 3 port keeps using that same global (see the PR for why we did not
// switch to the npm `ol` package yet), and moves the map/layer bookkeeping into
// this factory so the component only deals with Vue state.

import { fetchGpxDownload } from './api'
import { convertColorToRgb } from './logic'

export const JAPAN_EXTENT = [14282105.861, 3712719.7049, 16265799.6191, 5726050.6629]

export function createMapManager(vm, options) {
  const backgroundMaps = vm.backgroundMaps
  // The main geometry loader is configurable: `line_map` uses KML
  // (`/kml_compressed/...`, the default), `collabo_line_map` uses GPX
  // (`/gpx/...`). The detail loader is always KML (only `line_map` uses it).
  const geometryFormat = options.format || new ol.format.KML({ extractStyles: false })
  const kml = new ol.format.KML({ extractStyles: false })
  // `line_map` lazily loads the detailed geometry when the map moves
  // (`queueDetail`); `collabo_line_map` has no such queue, so it disables this.
  const withDetail = options.withDetail !== false
  const layers = {}
  let destroyed = false
  // AbortControllers for in-flight fetches; the legacy code aborted its jQuery
  // AJAX requests on `turbo:before-visit`.
  const controllers = []

  const backgroundMapLayer = new ol.layer.Tile({
    source: backgroundMaps.gsiPlain.createSource(),
  })
  backgroundMapLayer.setZIndex(1)

  const view = new ol.View()

  const map = new ol.Map({
    target: options.target,
    layers: [backgroundMapLayer],
    view: view,
    controls: [
      new ol.control.ScaleLine(),
      new ol.control.FullScreen(),
      new ol.control.Zoom(),
      new ol.control.Attribution({ collapsible: true }),
    ],
  })

  // The legacy handler referred to the outer `layers`/`map` closure. Keeping it
  // as a closure here preserves the original `self` binding (`vm`).
  const onMoveEnd = function () {
    const zoom_level = map.getView().getZoom()
    if (zoom_level > 10) {
      const map_extent = map.getView().calculateExtent(map.getSize())
      for (const route of vm.routes) {
        const layer = layers[route.id]
        if (!layer) continue
        const extent = layer.getSource().getExtent()
        if (
          (map_extent[0] < extent[2] || extent[0] < map_extent[2]) &&
          map_extent[1] < extent[3] &&
          extent[1] < map_extent[3]
        ) {
          vm.queueDetail(route.id)
        }
      }
    }
  }
  if (withDetail) map.on('moveend', onMoveEnd)

  view.fit(JAPAN_EXTENT, { padding: [0, 0, 0, options.getPadding()] })

  // Registers an AbortController so its request can be aborted on teardown.
  function registerRequest(controller) {
    controllers.push(controller)
  }

  function abortRequests() {
    while (controllers.length > 0) {
      const controller = controllers.pop()
      if (controller) controller.abort()
    }
  }

  function changeBackgroundMap(id) {
    backgroundMapLayer.setSource(backgroundMaps[id].createSource())
  }

  function resetExtent() {
    map.getView().fit(JAPAN_EXTENT, { padding: [0, 0, 0, options.getPadding()] })
  }

  function updateExtent(routes) {
    const extents = []
    for (const route of routes) {
      if (route.selected) {
        const layer = layers[route.id]
        if (!layer) continue
        extents.push(layer.getSource().getExtent())
      }
    }
    if (extents.length === 0) return
    const extent = extents.reduce(function (a, b) {
      return [
        Math.min(a[0], b[0]),
        Math.min(a[1], b[1]),
        Math.max(a[2], b[2]),
        Math.max(a[3], b[3]),
      ]
    })
    map.getView().fit(extent, { padding: [0, 0, 0, options.getPadding()], duration: 1000 })
  }

  function addRouteLayer(id, color) {
    const route = new ol.layer.Vector({
      source: new ol.source.Vector(),
      style: new ol.style.Style({
        stroke: new ol.style.Stroke({ color: color, width: 3 }),
      }),
    })
    route.setZIndex(2)
    map.addLayer(route)
    layers[id] = route
    return route
  }

  // Fetches a route document (KML or GPX, per `options.format`) and replaces
  // the features of the route layer with it. `title` is only used for the same
  // error alert the legacy code showed.
  function loadRoute(url, id, title, signal) {
    return fetchGpxDownload(url, signal).then(
      function (data) {
        const layer = layers[id]
        if (!layer) return
        layer.getSource().addFeatures(
          geometryFormat.readFeatures(data, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          })
        )
      },
      function (error) {
        if (error && error.name === 'AbortError') return
        alert(`読み込みエラー: ${title}\n一度削除して再追加すると解決することがあります`)
        throw new Error(`Failed to load ${url}`)
      }
    )
  }

  // Replaces the route layer's features with the detailed KML. The legacy
  // `loadDetailRoute` referenced an undefined `title` in its error alert as
  // well; behaviour kept as-is.
  function loadKmlDetails(url, id, signal) {
    return fetchGpxDownload(url, signal).then(
      function (data) {
        const layer = layers[id]
        if (!layer) return
        layer.getSource().clear()
        layer.getSource().addFeatures(
          kml.readFeatures(data, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          })
        )
      },
      function (error) {
        if (error && error.name === 'AbortError') return
        alert(`読み込みエラー: ${title}\n一度削除して再追加すると解決することがあります`)
        throw new Error(`Failed to load ${url}`)
      }
    )
  }

  function changeColor(id, color) {
    const layer = layers[id]
    if (!layer) return
    layer.setStyle(
      new ol.style.Style({
        stroke: new ol.style.Stroke({ color: color, width: 5 }),
      })
    )
  }

  function removeRouteLayer(id) {
    const layer = layers[id]
    if (!layer) return
    map.removeLayer(layer)
    delete layers[id]
  }

  function updateLayerStyles(routes) {
    map.updateSize()
    const is_selected_exists =
      routes.filter(function (r) {
        return r.selected
      }).length > 0
    for (const route of routes) {
      const layer = layers[route.id]
      if (!layer) continue
      if (route.selected) {
        layer.setZIndex(3)
        layer.setStyle(
          new ol.style.Style({
            stroke: new ol.style.Stroke({ color: route.color, width: 5 }),
          })
        )
      } else {
        let [re, gr, bl] = convertColorToRgb(route.color)
        re = (255 - re) * 0.5 + re
        gr = (255 - gr) * 0.5 + gr
        bl = (255 - bl) * 0.5 + bl
        layer.setZIndex(2)
        layer.setStyle(
          new ol.style.Style({
            stroke: new ol.style.Stroke({
              color: is_selected_exists ? [re, gr, bl, 1.0] : route.color,
              width: 3,
            }),
          })
        )
      }
    }
  }

  function destroy() {
    if (destroyed) return
    destroyed = true
    abortRequests()
    map.un('moveend', onMoveEnd)
    map.setTarget(null)
    for (const id of Object.keys(layers)) {
      delete layers[id]
    }
  }

  return {
    map,
    registerRequest: registerRequest,
    abortRequests: abortRequests,
    changeBackgroundMap: changeBackgroundMap,
    resetExtent: resetExtent,
    updateExtent: updateExtent,
    addRouteLayer: addRouteLayer,
    loadRoute: loadRoute,
    loadKmlDetails: loadKmlDetails,
    changeColor: changeColor,
    removeRouteLayer: removeRouteLayer,
    updateLayerStyles: updateLayerStyles,
    destroy: destroy,
  }
}
